<div class="flex flex-col gap-8 max-w-3xl">
    <!-- Profile Details Section -->
    <div class="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
        <h3 class="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="text-primary">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
            </svg>
            {{ 'profile_details' | translate }}
        </h3>

        <form [formGroup]="profileForm" (ngSubmit)="updateProfile()" class="space-y-6">

            <!-- Photo Upload -->
            <div class="flex items-center gap-6">
                <div class="relative group">
                    <div class="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-200">
                        <img [src]="photoPreview || currentPhoto || 'assets/images/user-profile.png'"
                            class="w-full h-full object-cover" alt="Profile Photo">
                    </div>
                    <button type="button" (click)="fileInput.click()"
                        class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path
                                d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                            <circle cx="12" cy="13" r="4" />
                        </svg>
                    </button>
                </div>
                <div>
                    <h4 class="font-semibold text-slate-800">{{ 'change_photo' | translate }}</h4>
                    <p class="text-xs text-slate-500 mb-2">{{ 'profile_photo_hint' | translate }}</p>
                    <button type="button" (click)="fileInput.click()"
                        class="text-primary text-sm font-medium hover:underline">
                        {{ 'upload_new' | translate }}
                    </button>
                    <input #fileInput type="file" class="hidden" accept="image/*" (change)="onFileSelected($event)">
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label class="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">{{ 'user_name'
                        | translate }}</label>
                    <input type="text" formControlName="name"
                        class="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all shadow-sm text-sm"
                        [class.border-red-300]="isFieldInvalid(profileForm, 'name')">
                </div>

                <div>
                    <label class="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">{{
                        'user_phone' | translate }}</label>
                    <!-- No [appendTo]="null": the control's own wrapper is
                         overflow-hidden (it clips the select to the rounded
                         border), so an inline panel is clipped away entirely and
                         the country picker never appears. 'body' is the default. -->
                    <app-phone-input formControlName="phone"
                        [invalid]="isFieldInvalid(profileForm, 'phone')"></app-phone-input>
                </div>

                <div class="md:col-span-2">
                    <label class="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">{{
                        'user_email' | translate }}</label>
                    <input type="email" [value]="currentUserEmail" disabled
                        class="w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 shadow-sm text-sm cursor-not-allowed">
                    <p class="text-xs text-slate-400 mt-1 ml-1">{{ 'email_cannot_be_changed' | translate }}</p>
                </div>
            </div>

            <div class="flex justify-end mt-4 w-full">
                <app-button [label]="'save' | translate" [loading]="savingProfile" (onClick)="updateProfile()"
                    variant="primary" styleClass="min-w-[160px]">
                </app-button>
            </div>
        </form>
    </div>

    <!-- Change Password Section -->
    <div class="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
        <h3 class="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="text-primary">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {{ 'change_password' | translate }}
        </h3>

        <form [formGroup]="passwordForm" (ngSubmit)="updatePassword()" class="space-y-6">
            <div>
                <label class="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">{{ 'new_password'
                    | translate }}</label>
                <div class="relative">
                    <input [type]="showPassword ? 'text' : 'password'" formControlName="password"
                        class="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all shadow-sm text-sm"
                        [class.border-red-300]="isFieldInvalid(passwordForm, 'password')">
                    <button type="button" (click)="togglePasswordVisibility()"
                        class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <svg *ngIf="!showPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path
                                d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" />
                        </svg>
                        <svg *ngIf="showPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                    </button>
                </div>
                <app-password-checklist [value]="passwordForm.get('password')?.value"
                    [touched]="!!passwordForm.get('password')?.touched"></app-password-checklist>
            </div>

            <div>
                <label class="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">{{
                    'confirm_password' | translate }}</label>
                <div class="relative">
                    <input [type]="showConfirmPassword ? 'text' : 'password'" formControlName="confirmPassword"
                        class="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all shadow-sm text-sm"
                        [class.border-red-300]="isFieldInvalid(passwordForm, 'confirmPassword') || passwordForm.errors?.['mismatch']">
                    <button type="button" (click)="showConfirmPassword = !showConfirmPassword"
                        class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <svg *ngIf="!showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path
                                d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" />
                        </svg>
                        <svg *ngIf="showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                    </button>
                </div>
                <p *ngIf="passwordForm.errors?.['mismatch'] && passwordForm.get('confirmPassword')?.touched"
                    class="text-xs text-red-500 mt-1 ml-1">{{ 'passwords_do_not_match' | translate }}</p>
            </div>

            <div class="flex justify-end mt-4 w-full">
                <app-button [label]="'update_password' | translate" [loading]="updatingPassword"
                    (onClick)="updatePassword()" variant="primary" styleClass="min-w-[160px]">
                </app-button>
            </div>
        </form>
    </div>
</div>